import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities, deleteEntity } from '@redux/entities'

import api from '@functions/api'

import type { BrandGroupMemberModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/brandGroupMember/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/brandGroupMember/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/brandGroupMember/FETCH_FAILURE'

const CREATE_REQUEST = 'realhub/brandGroupMember/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/brandGroupMember/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/brandGroupMember/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/brandGroupMember/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/brandGroupMember/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/brandGroupMember/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/brandGroupMember/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/brandGroupMember/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/brandGroupMember/DELETE_FAILURE'

export type BrandGroupMemberModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: Errors,
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: BrandGroupMemberModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}): Action {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess(): Action {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []): Action {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(): Action {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(): Action {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []): Action {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(): Action {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(): Action {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []): Action {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(): Action {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(): Action {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []): Action {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadBrandGroupMembers(options: EntityOptions = {}) {
  const { entityKey } = options || {}

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().brandGroupMembers.loadedForKeys]
    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/brand_group_members.json', options)
      .then(({ data }: { data: BrandGroupMemberModel[] }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_GROUP_MEMBER_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteBrandGroupMember(brandGroupMember: BrandGroupMemberModel) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/brand_group_members/${brandGroupMember.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_GROUP_MEMBER)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })
    return promise
  }
}

export function bulkCreateBrandGroupMembers(
  params: { brandGroupId: number, memberIds: number[] },
  options: EntityOptions = {},
) {
  const { brandGroupId, memberIds } = params

  const config = {
    method: 'POST',
    data: JSON.stringify({
      brand_group_id: brandGroupId,
      member_ids: memberIds,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/brand_group_members/bulk_create.json', options, config)
      .then(({ data }: { data: BrandGroupMemberModel[] }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_GROUP_MEMBER_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function setAdmin(brandGroupMember: BrandGroupMemberModel, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/brand_group_members/${brandGroupMember.id}/set_admin.json`, options, config)
      .then(({ data }: { data: BrandGroupMemberModel[] }) => {
        const normalizedJson = normalize(data, Schemas.BRAND_GROUP_MEMBER_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: BrandGroupMemberModuleState = initialState,
  action: Action = { type: '' },
): BrandGroupMemberModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loading: true,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
